import { ONE_CHANCE_SETTINGS } from "app/data/game-settings";
import type { WordData } from "app/game-data/useFetchGameData";
import { RoundType } from "app/game-data/useFetchGameData";
import useGameData from "app/game-data/useGameData";
import { useGameState } from "app/game/useGame";
import LetterBoxes, { NUM_LETTERS_PER_ROW } from "app/one-chance/LetterBoxes";
import LockableAudioButton from "app/one-chance/LockableAudioButton";
import { OneChanceProvider } from "app/one-chance/OneChanceProvider";
import { OneChanceAction } from "app/one-chance/oneChanceState";
import {
  parseUserSpelling,
  revealChar,
  tryMoveCaret,
  tryPlaceCaret,
  tryBackspace,
  getMaxCaretPos,
  checkIsEditable,
  revealLength,
} from "app/one-chance/oneChanceSpellingUtils";
import ScoreDisplay, {
  type ScoreDisplayHandle,
} from "app/one-chance/ScoreDisplay";
import {
  useOneChanceActionDispatch,
  useOneChanceState,
} from "app/one-chance/useOneChance";
import { ScoreAction } from "app/score/scoreState";
import { useScoreDispatch, useScoreState } from "app/score/useScore";
import Keyboard from "app/spelling-input/Keyboard";
import NextRoundButton from "app/ui/NextRoundButton";
import { type PlayAudioButtonHandle } from "app/ui/PlayAudioButton";
import RoundIntroScreen from "app/ui/RoundIntroScreen";
import cn from "app/utils/classnames";
import mixpanel from "mixpanel-browser";
import { useCallback, useEffect, useRef, useState } from "react";

interface OneChanceRoundProps {
  word: WordData;
  roundIndex: number;
  onComplete: () => void;
  className?: string;
}

function OneChanceRound({
  word,
  roundIndex,
  onComplete,
  className,
}: OneChanceRoundProps) {
  const gameData = useGameData();
  const { inputType } = useGameState();
  const scoreState = useScoreState();
  const {
    currentSpelling,
    scoreMultiplier,
    spellingSubmitted,
    revealsUsed,
    originalScore,
    definitionUnlocked,
    originUnlocked,
    isOnIntro,
  } = useOneChanceState();

  const roundDispatch = useOneChanceActionDispatch();
  const scoreDispatch = useScoreDispatch();

  const [caretPos, setCaretPos] = useState(0);

  const scoreDisplayRef = useRef<ScoreDisplayHandle>(null);
  const wordButtonRef = useRef<PlayAudioButtonHandle>(null);
  const defButtonRef = useRef<PlayAudioButtonHandle>(null);
  const etyButtonRef = useRef<PlayAudioButtonHandle>(null);

  const currentMult = scoreMultiplier ?? ONE_CHANCE_SETTINGS.initMult;
  const roundScore = scoreState.roundScores[roundIndex]?.score;
  const roundComplete =
    spellingSubmitted && roundScore != null && roundScore != -1;

  function handleAudioButtonClick(
    buttonRef: React.RefObject<PlayAudioButtonHandle | null>,
  ) {
    if (buttonRef.current !== wordButtonRef.current) {
      wordButtonRef.current?.forceStop();
    }
    if (buttonRef.current !== defButtonRef.current) {
      defButtonRef.current?.forceStop();
    }
    if (buttonRef.current !== etyButtonRef.current) {
      etyButtonRef.current?.forceStop();
    }
  }

  function handleScoreAnimFinished() {
    if (roundComplete) {
      return;
    }

    const pointsEarned =
      scoreState.roundScores[roundIndex].wordSpellings[0].pointValue;

    scoreDispatch({
      type: ScoreAction.POINTS_EARNED,
      payload: { roundIndex: roundIndex, points: pointsEarned ?? 0 },
    });
  }

  // Allow use of arrow keys to move cursor
  useEffect(() => {
    function onArrowKeyDown(e: KeyboardEvent) {
      switch (e.key) {
        case "ArrowLeft":
          setCaretPos((prev) => tryMoveCaret(currentSpelling, prev, -1));
          break;
        case "ArrowRight":
          setCaretPos((prev) => tryMoveCaret(currentSpelling, prev, 1));
          break;
        case "ArrowDown":
          setCaretPos((prev) =>
            tryPlaceCaret(currentSpelling, prev + NUM_LETTERS_PER_ROW),
          );
          break;
        case "ArrowUp":
          setCaretPos((prev) =>
            tryPlaceCaret(currentSpelling, prev - NUM_LETTERS_PER_ROW),
          );
          break;
      }
    }

    window.addEventListener("keydown", onArrowKeyDown);
    return () => {
      window.removeEventListener("keydown", onArrowKeyDown);
    };
  }, [currentSpelling]);

  // Insert a character when a letter key is pressed
  const handleKeyboardLetterDown = useCallback(
    (letter: string) => {
      const currentChar = currentSpelling[caretPos];

      // Only replace the selected character if it is editable.
      if (checkIsEditable(currentChar)) {
        const newSpelling =
          currentSpelling.slice(0, caretPos) +
          letter.toLowerCase() +
          currentSpelling.slice(caretPos + 1);
        roundDispatch({
          type: OneChanceAction.SPELLING_UPDATED,
          payload: { newSpelling },
        });

        // Move the caret forward
        setCaretPos((prev) => tryMoveCaret(newSpelling, prev, 1));
      }
    },
    [caretPos, currentSpelling, roundDispatch],
  );

  // When backspace is pressed on the onscreen or physical keyboard
  const handleKeyboardBackspaceDown = useCallback(() => {
    const { newSpelling, newCaretPos } = tryBackspace(
      currentSpelling,
      caretPos,
    );

    roundDispatch({
      type: OneChanceAction.SPELLING_UPDATED,
      payload: { newSpelling },
    });

    setCaretPos(newCaretPos);
  }, [caretPos, roundDispatch, currentSpelling]);

  // When enter is pressed on the onscreen or physical keyboard
  const handleKeyboardEnterDown = useCallback(() => {
    const playerSpelling = parseUserSpelling(currentSpelling);

    // Don't submit if no letters have been entered
    if (playerSpelling.length === 0) {
      return;
    }
    
    // Determine scoring
    const correctSpelling = word.wordText.toLowerCase();
    const isCorrect = playerSpelling === correctSpelling;
    const newPoints = isCorrect
      ? Math.ceil(scoreState.gameScore * currentMult)
      : scoreState.gameScore;
    const pointsToAdd = newPoints - scoreState.gameScore;

    // Stop all audio
    wordButtonRef.current?.forceStop();
    defButtonRef.current?.forceStop();
    etyButtonRef.current?.forceStop();

    roundDispatch({
      type: OneChanceAction.SPELLING_SUBMITTED,
      payload: {
        preMultScore: scoreState.gameScore,
        userSpelling: playerSpelling,
        isCorrect: isCorrect,
      },
    });

    scoreDispatch({
      type: ScoreAction.WORD_SPELLED,
      payload: {
        roundIndex: roundIndex,
        userSpelling: playerSpelling,
        correctSpelling: correctSpelling,
        pointValue: pointsToAdd,
        multiplier: currentMult,
      },
    });

    mixpanel.track("spelling_submitted", {
      game_id: gameData?.id,
      level_type: RoundType.ONE_CHANCE,
      user_spelling: playerSpelling,
      correct_spelling: correctSpelling,
    });

    // If we ever decide that OneChance doesn't always need to be the
    // final level type, we'll have to add more complex logic for this.
    // For now, this will work fine.
    mixpanel.track("game_completed", {
      game_id: gameData?.id,
      final_score: scoreState.gameScore,
    });
  }, [
    gameData,
    currentMult,
    currentSpelling,
    roundIndex,
    roundDispatch,
    scoreDispatch,
    scoreState.gameScore,
    word.wordText,
  ]);

  // When the player spends multipler to reveal a letter
  const handleLetterRevealed = useCallback(
    (revealedLetterIndex: number) => {
      mixpanel.track("onechance_letter_revealed", {
        game_id: gameData?.id,
      });

      const multCost =
        Math.round(
          (ONE_CHANCE_SETTINGS.letterRevealMultCost +
            revealsUsed * ONE_CHANCE_SETTINGS.letterRevealMultCostIncrease) *
            10,
        ) / 10;
      scoreDisplayRef.current?.playMultLoss(parseFloat(multCost.toFixed(1)));

      const newSpelling = revealChar(
        currentSpelling,
        word.wordText.toLowerCase(),
        revealedLetterIndex,
      );

      roundDispatch({
        type: OneChanceAction.SPELLING_UPDATED,
        payload: { newSpelling },
      });

      roundDispatch({ type: OneChanceAction.LETTER_REVEALED });

      // If the caret is currently on the revealed character, move it to the next
      // available space.
      setCaretPos((prev) => tryPlaceCaret(newSpelling, prev));
    },
    [gameData, currentSpelling, revealsUsed, word.wordText, roundDispatch],
  );

  // When the player spends multiplier to reveal the length of the correct
  // spelling.
  const handleLengthRevealed = useCallback(() => {
    mixpanel.track("onechance_length_revealed", {
      game_id: gameData?.id,
    });

    scoreDisplayRef.current?.playMultLoss(ONE_CHANCE_SETTINGS.lengthMultCost);

    const newSpelling = revealLength(currentSpelling, word.wordText.toLowerCase());
    roundDispatch({
      type: OneChanceAction.SPELLING_UPDATED,
      payload: { newSpelling },
    });
    roundDispatch({ type: OneChanceAction.LENGTH_REVEALED });

    setCaretPos((prev) => tryPlaceCaret(newSpelling, prev));
  }, [gameData, currentSpelling, word.wordText, roundDispatch]);

  // When the player taps a letter box to move the cursor
  function handleLetterBoxTapped(letterIndex: number) {
    setCaretPos(() => Math.min(letterIndex, getMaxCaretPos(currentSpelling)));
  }

  return (
    <div
      className={cn(
        "h-full",
        "flex flex-col items-center gap-3 lg:gap-5",
        "relative",
        className,
      )}
    >
      <div className={cn("w-full flex-1 lg:flex-none", "flex flex-col gap-4")}>
        <ScoreDisplay
          ref={scoreDisplayRef}
          startingScore={originalScore}
          multiplier={currentMult}
          playAnimation={spellingSubmitted}
          onAnimFinished={handleScoreAnimFinished}
        />
        <div className={cn("flex justify-center gap-3")}>
          <LockableAudioButton
            isLocked={false}
            audioUrl={word.wordAudioUrl ?? ""}
            onClick={() => handleAudioButtonClick(wordButtonRef)}
            buttonRef={wordButtonRef}
            isDisabled={spellingSubmitted}
          >
            Hear Word
          </LockableAudioButton>
          <LockableAudioButton
            key={word.definitionAudioUrl}
            audioUrl={word.definitionAudioUrl ?? ""}
            onClick={() => handleAudioButtonClick(defButtonRef)}
            buttonRef={defButtonRef}
            isDisabled={spellingSubmitted}
            isLocked={!definitionUnlocked && !spellingSubmitted}
            unlockCost={ONE_CHANCE_SETTINGS.definitionMultCost}
            onUnlock={() => {
              scoreDisplayRef.current?.playMultLoss(
                ONE_CHANCE_SETTINGS.definitionMultCost,
              );
              mixpanel.track("onechance_definition_unlocked", {
                game_id: gameData?.id,
              });
              roundDispatch({ type: OneChanceAction.DEFINITION_UNLOCKED });
            }}
          >
            Hear Meaning
          </LockableAudioButton>
          <LockableAudioButton
            key={word.originAudioUrl}
            audioUrl={word.originAudioUrl ?? ""}
            onClick={() => handleAudioButtonClick(etyButtonRef)}
            buttonRef={etyButtonRef}
            isDisabled={spellingSubmitted}
            isLocked={!originUnlocked && !spellingSubmitted}
            unlockCost={ONE_CHANCE_SETTINGS.originMultCost}
            onUnlock={() => {
              scoreDisplayRef.current?.playMultLoss(
                ONE_CHANCE_SETTINGS.originMultCost,
              );
              mixpanel.track("onechance_origin_unlocked", {
                game_id: gameData?.id,
              });
              roundDispatch({ type: OneChanceAction.ORIGIN_UNLOCKED });
            }}
          >
            Hear Origin
          </LockableAudioButton>
        </div>
        {!isOnIntro && (
          <LetterBoxes
            playerSpelling={currentSpelling}
            correctSpelling={word.wordText.toLowerCase()}
            isInteractive={!spellingSubmitted}
            onLetterRevealed={handleLetterRevealed}
            onLetterTapped={handleLetterBoxTapped}
            onLengthRevealed={handleLengthRevealed}
            caretPos={caretPos}
          />
        )}
      </div>
      {roundComplete ? (
        <div className="flex-1 flex flex-col gap-2 mt-1">
          <p // CORRECT/INCORRECT FEEDBACK
            className={cn("mb-1.5")}
          >
            {scoreState.roundScores[roundIndex]?.wordSpellings[0].pointValue ===
            0 ? (
              <>
                Your score stays at{" "}
                <b>{scoreState.gameScore.toLocaleString()}</b>.
              </>
            ) : (
              <>
                Your score has increased to{" "}
                <b>{scoreState.gameScore.toLocaleString()}!</b>
              </>
            )}
          </p>
          <NextRoundButton
            className="mx-auto"
            onClick={() => {
              roundDispatch({ type: OneChanceAction.ROUND_COMPLETED });
              onComplete();
            }}
            allowEnter={true}
          />
        </div>
      ) : (
        !isOnIntro &&
        !spellingSubmitted && (
          <div
            className={cn(
              "w-full",
              "grow",
              "flex items-end justify-center",
              inputType === "mouse" && "hidden",
            )}
          >
            <Keyboard
              onLetterDown={handleKeyboardLetterDown}
              onBackspaceDown={handleKeyboardBackspaceDown}
              onEnterDown={handleKeyboardEnterDown}
              allowPhysKeyPresses={true}
            />
          </div>
        )
      )}
      {isOnIntro && (
        <RoundIntroScreen
          className="absolute top-0 bottom-0 -left-2 -right-2 px-5 lg:px-1"
          roundType={RoundType.ONE_CHANCE}
          roundIndex={roundIndex}
          onContinueClick={() => {
            wordButtonRef.current?.forcePlay();
            roundDispatch({ type: OneChanceAction.INTRO_CLOSED });
          }}
        />
      )}
    </div>
  );
}

export default function WrappedOneChanceRound(props: OneChanceRoundProps) {
  return (
    <OneChanceProvider key={props.roundIndex} roundIndex={props.roundIndex}>
      <OneChanceRound {...props} />
    </OneChanceProvider>
  );
}
